import React, { useState } from 'react';
import { Box, Typography, Grid, Card, CardActionArea, CardContent, Avatar, Button, Alert } from '@mui/material';
import GroupIcon from '@mui/icons-material/Group';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';
import QrCodeIcon from '@mui/icons-material/QrCode';
import LogoutIcon from '@mui/icons-material/Logout';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import EmailIcon from '@mui/icons-material/Email';
import { useNavigate } from 'react-router-dom';
import { authService, adminService } from '../../services/api';

const cards = [
  {
    title: 'Usuarios',
    description: 'Ver y gestionar todos los usuarios registrados',
    icon: <GroupIcon fontSize="large" />, 
    color: '#7C3AED',
    to: '/admin/users',
  },
  {
    title: 'Actividades',
    description: 'Revisar y aprobar/rechazar actividades de los niños',
    icon: <AssignmentTurnedInIcon fontSize="large" />, 
    color: '#22C55E',
    to: '/admin/activities',
  },
  {
    title: 'Generar Códigos',
    description: 'Crear códigos únicos para desbloquear libros',
    icon: <QrCodeIcon fontSize="large" />, 
    color: '#F59E0B',
    to: '/admin/codes',
  },
  {
    title: 'Sistema Financiero',
    description: 'Ver todas las características del sistema financiero, estrellas y Tfiats',
    icon: <MonetizationOnIcon fontSize="large" />, 
    color: '#10B981',
    to: '/admin/financial-system',
  },
  {
    title: 'Distribución Tfiats',
    description: 'Ver tabla completa de distribución y asignar manualmente',
    icon: <AccountBalanceIcon fontSize="large" />, 
    color: '#8B5CF6',
    to: '/admin/tfiat-distribution',
  },
  {
    title: 'Tesorería',
    description: 'Verificar balances del sistema, conversión a euros y desbalances',
    icon: <AccountBalanceWalletIcon fontSize="large" />, 
    color: '#DC2626',
    to: '/admin/treasury',
  },
  {
    title: 'Lista de Espera',
    description: 'Ver y gestionar la lista de espera de Kickstarter',
    icon: <EmailIcon fontSize="large" />, 
    color: '#3B82F6',
    to: '/admin/waitlist',
  },
];

const AdminDashboard: React.FC = () => {
  const navigate = useNavigate();
  const [message, setMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null);

  const handleLogout = async () => {
    try {
      const refreshToken = localStorage.getItem('refreshToken');
      if (refreshToken) {
        await authService.logout(refreshToken);
      }
      navigate('/login');
    } catch (error) {
      console.error('Error al cerrar sesión:', error);
      localStorage.removeItem('token');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('userId');
      localStorage.removeItem('dashboardData');
      localStorage.removeItem('authUser');
      navigate('/login');
    }
  };

  return (
    <Box sx={{ p: 4, minHeight: '100vh', bgcolor: '#f5f5f5' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Typography variant="h4" fontWeight={700} color="primary">
          🎯 Panel Administrativo
        </Typography>
        <Button
          variant="outlined"
          color="error"
          startIcon={<LogoutIcon />}
          onClick={handleLogout}
        >
          Cerrar Sesión
        </Button>
      </Box>

      {/* Mensajes de estado */}
      {message && (
        <Alert 
          severity={message.type} 
          sx={{ mb: 4 }}
          onClose={() => setMessage(null)}
        >
          {message.text}
        </Alert>
      )}

      {/* Grid de tarjetas */}
      <Grid container spacing={4} justifyContent="center">
        {cards.map((card) => (
          <Grid item xs={12} sm={6} md={4} key={card.title}>
            <Card elevation={6} sx={{ border: `3px solid ${card.color}`, background: '#fff', transition: 'transform 0.2s', '&:hover': { transform: 'translateY(-8px) scale(1.03)' } }}>
              <CardActionArea onClick={() => navigate(card.to)}>
                <CardContent sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 5 }}>
                  <Avatar sx={{ bgcolor: card.color, width: 72, height: 72, mb: 2 }}>
                    {card.icon}
                  </Avatar>
                  <Typography variant="h5" fontWeight={600} color="primary" gutterBottom>
                    {card.title}
                  </Typography>
                  <Typography variant="body1" color="text.secondary" align="center">
                    {card.description}
                  </Typography>
                </CardContent>
              </CardActionArea>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

export default AdminDashboard; 